import type { ReactNode } from 'react';
import { FieldSize, Size, t } from '@bms2/shared';
import { IconButton, Select } from '../controls';
import { Icon } from '../Icon';
import styles from './CriteriaRows.module.css';

/** One Field / Comparator / Value row, whichever builder holds it. */
export interface CriteriaRowShape {
  id: string;
  field: string;
  comparator: string;
  value: string;
}

/** Why a row is refused at Save, and where the reason reads: under the field, or under the value (decision 2172). */
export interface CriteriaRowProblem {
  at: 'field' | 'value';
  text: string;
}

export interface CriteriaRowsProps<R extends CriteriaRowShape> {
  rows: readonly R[];
  fields: readonly { value: string; label: string }[];
  /** The field select's empty choice, where a new row starts with no field chosen (decision 2172). */
  fieldPlaceholder?: string;
  /** The comparators a row's field offers. */
  comparators: (field: string) => readonly { value: string; label: string }[];
  /** The value control for a row, typed the way its field reads, handed the reason it is refused; null draws no value box. */
  renderValue: (row: R, error: string | null) => ReactNode;
  /** The row's refusal at Save, or null. */
  problem?: (row: R) => CriteriaRowProblem | null;
  onChange: (id: string, patch: Partial<Pick<R, 'field' | 'comparator'>>) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
}

/**
 * The Field / Comparator / Value criteria-row builder (piece 5), drawn once for
 * the New View Modal and for mail's Filter Rules (piece 32; decision 0834), so a
 * second rule builder is never written. The holder keeps the rows; this draws
 * and edits them. 🔒 On a desk a row fills the band with a cross at its end; on
 * a phone it is one bordered card (decision 2175).
 */
export function CriteriaRows<R extends CriteriaRowShape>({ rows, fields, fieldPlaceholder, comparators, renderValue, problem, onChange, onRemove, onAdd }: CriteriaRowsProps<R>) {
  return (
    <div className={styles.rows} data-criteria-rows={rows.length}>
      {rows.map((row) => {
        const refused = problem?.(row) ?? null;
        const value = renderValue(row, refused?.at === 'value' ? refused.text : null);
        return (
          <div key={row.id} className={styles.row} data-criteria-row={row.id} data-criteria-problem={refused?.at}>
            <div className={styles.pair}>
              <div className={styles.field}>
                <Select
                  size={FieldSize.Column}
                  label={t('view.field')}
                  labelHidden
                  value={row.field}
                  onChange={(next) => onChange(row.id, { field: next } as Partial<Pick<R, 'field' | 'comparator'>>)}
                  options={fields}
                  error={refused?.at === 'field' ? refused.text : null}
                  {...(fieldPlaceholder === undefined ? {} : { placeholder: fieldPlaceholder })}
                />
              </div>
              <div className={styles.comparator}>
                <Select
                  size={FieldSize.Column}
                  label={t('view.comparator')}
                  labelHidden
                  value={row.comparator}
                  onChange={(next) => onChange(row.id, { comparator: next } as Partial<Pick<R, 'field' | 'comparator'>>)}
                  options={comparators(row.field)}
                />
              </div>
            </div>
            <div className={styles.value}>{value}</div>
            <span className={styles.remove} data-remove-criteria={row.id}>
              <IconButton meaning="remove" label={t('view.removeRow')} size={Size.Small} onClick={() => onRemove(row.id)} name="remove-criteria" />
            </span>
          </div>
        );
      })}
      <button type="button" className={styles.add} data-add-criteria="true" onClick={onAdd}>
        <Icon meaning="add" size="inline" />
        {t('view.addRow')}
      </button>
    </div>
  );
}
